import { useRef, useCallback, useEffect } from 'react';
import { AUDIO_CONFIG } from '@/game/configs';
interface AudioCallbacks {
  playHit: () => void;
  playScore: () => void;
  playDodge: () => void;
}
type AudioContextOrNull = AudioContext | null;
export function useGameAudio(): AudioCallbacks {
  const audioContextRef = useRef<AudioContextOrNull>(null);
  const isInitializedRef = useRef(false);
  const initAudioContext = useCallback(() => {
    if (isInitializedRef.current) return;
    try {
      const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      audioContextRef.current = ctx;
      isInitializedRef.current = true;
    } catch {
      console.warn('[Audio] Web Audio API not available');
    }
  }, []);
  const resumeContext = useCallback(() => {
    const ctx = audioContextRef.current;
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
  }, []);
  useEffect(() => {
    const handleInteraction = () => {
      initAudioContext();
      resumeContext();
    };
    window.addEventListener('pointerdown', handleInteraction, { once: true });
    window.addEventListener('touchstart', handleInteraction, { once: true });
    window.addEventListener('keydown', handleInteraction, { once: true });
    return () => {
      window.removeEventListener('pointerdown', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };
  }, [initAudioContext, resumeContext]);
  const playSound = useCallback(
    (config: { frequency: number; duration: number; volume: number; type: OscillatorType }) => {
      const ctx = audioContextRef.current;
      if (!ctx) return;
      try {
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.type = config.type;
        oscillator.frequency.setValueAtTime(config.frequency, ctx.currentTime);
        gainNode.gain.setValueAtTime(config.volume, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.001,
          ctx.currentTime + config.duration
        );
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + config.duration);
      } catch {
        // Silently fail - audio is non-critical
      }
    },
    []
  );
  const playHit = useCallback(() => {
    playSound(AUDIO_CONFIG.hit);
  }, [playSound]);
  const playScore = useCallback(() => {
    playSound(AUDIO_CONFIG.score);
  }, [playSound]);
  const playDodge = useCallback(() => {
    playSound(AUDIO_CONFIG.dodge);
  }, [playSound]);
  return { playHit, playScore, playDodge };
}